import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { toast } from "react-toastify";
import { errorMessage, saveWatch } from "./api";
import { fmtDayTime } from "../home/time";

export const ATTEND_PERCENT = 60; // fallback; the server sends lesson.attend_percent (an editable setting)

/** vimeo.com/123 or vimeo.com/123/abcdef (unlisted) → player URL; null for anything else. */
export const vimeoEmbed = (url) => {
  const m = /vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-z0-9]+))?/i.exec(url || "");
  return m ? `https://player.vimeo.com/video/${m[1]}${m[2] ? `?h=${m[2]}` : ""}` : null;
};
export const isVideoFile = (url) => /\.(mp4|webm|m4v|mov)(\?|$)/i.test(url || "");
const clock = (sec) => { const s = Math.floor(sec); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const r = String(s % 60).padStart(2, "0"); return h ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`; };

// This device's last spot per lesson, so a closed tab still resumes even if its last save didn't reach the server.
const POS_KEY = (id) => `e1x-rec-pos-${id}`;
const readLocalPos = (id) => { try { const v = JSON.parse(localStorage.getItem(POS_KEY(id)) || "null"); return v && typeof v.pos === "number" ? v.pos : null; } catch { return null; } };
const writeLocalPos = (id, pos) => { try { localStorage.setItem(POS_KEY(id), JSON.stringify({ pos, at: Date.now() })); } catch { /* storage off */ } };

/**
 * Class recording stage (prototype `.stage.rec`). Counts real playback only (seeking ahead doesn't
 * count) and saves it; 60% watched marks attendance and completes the lesson. Recordings hosted
 * elsewhere open in a new tab with an "I've watched it" confirmation.
 */
export default function RecordingPlayer({ lesson, onProgress }) {
  const iframeRef = useRef(null);
  const [percent, setPercent] = useState(lesson.watch_percent || 0);
  const [busy, setBusy] = useState(false);
  const track = useRef({ last: null, session: 0, prior: null, duration: null, saved: 0, pos: 0, savedPos: null, localPos: 0 });
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const [resumedAt, setResumedAt] = useState(null);
  // Where to pick up: this device's last spot if it has one, else the server's.
  const startAt = useRef(readLocalPos(lesson.id) ?? lesson.resume_at ?? 0);
  const embed = vimeoEmbed(lesson.recording_link);
  const file = !embed && isVideoFile(lesson.recording_link);
  const measured = embed || file;

  const save = async () => {
    const t = track.current;
    if (!t.duration) return;
    const pos = Math.round(t.pos);
    if (t.session - t.saved < 1 && (t.savedPos !== null && Math.abs(pos - t.savedPos) < 5)) return;
    t.saved = t.session;
    t.savedPos = pos;
    try {
      const res = await saveWatch(lesson.id, { watched_seconds: Math.round(t.prior + t.session), duration_seconds: t.duration, position_seconds: pos });
      if (res.complete && !lesson.complete && lesson.tracked !== false) {
        toast.success("Attendance marked from the recording · lesson complete");
        onProgress?.();
      }
    } catch { /* retried on the next save */ }
  };

  const onTime = (seconds, duration) => {
    const t = track.current;
    if (!duration) return;
    if (t.prior === null) t.prior = Math.round(((lesson.watch_percent || 0) / 100) * duration);
    t.duration = Math.round(duration);
    if (t.last !== null) {
      const step = seconds - t.last;
      if (step > 0 && step < 2) t.session += step;
    }
    t.last = seconds;
    t.pos = seconds;
    if (Math.abs(seconds - t.localPos) >= 2) { t.localPos = seconds; writeLocalPos(lesson.id, Math.round(seconds)); }
    setPercent((p) => Math.max(p, Math.min(100, Math.floor(((t.prior + t.session) / duration) * 100))));
    if (t.session - t.saved >= 15) save();
  };

  useEffect(() => {
    if (!embed || !iframeRef.current) return undefined;
    const player = new Player(iframeRef.current);
    playerRef.current = player;
    player.getDuration().then((d) => { const at = resumeTarget(d); if (at) player.setCurrentTime(at).then(() => setResumedAt(at)).catch(() => {}); }).catch(() => {});
    player.on("timeupdate", (d) => onTime(d.seconds, d.duration));
    player.on("pause", save);
    player.on("ended", save);
    return () => { save(); player.off("timeupdate"); player.off("pause"); player.off("ended"); };
  }, [embed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { save(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Save when the student switches tab or closes the page, not only on pause.
  useEffect(() => {
    const away = () => { if (document.visibilityState === "hidden") save(); };
    document.addEventListener("visibilitychange", away);
    window.addEventListener("pagehide", save);
    return () => { document.removeEventListener("visibilitychange", away); window.removeEventListener("pagehide", save); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Resume unless they'd barely started or were in the last 15 seconds (then start over). */
  function resumeTarget(duration) {
    const at = Math.round(startAt.current || 0);
    return duration && at > 5 && at < duration - 15 ? at : 0;
  }
  const startOver = () => {
    setResumedAt(null);
    track.current.last = null;
    if (playerRef.current) playerRef.current.setCurrentTime(0).catch(() => {});
    if (videoRef.current) videoRef.current.currentTime = 0;
  };

  const confirmWatched = async () => {
    setBusy(true);
    try {
      await saveWatch(lesson.id, { manual: true });
      toast.success("Attendance marked from the recording · lesson complete");
      onProgress?.();
    } catch (err) {
      toast.error(errorMessage(err));
    }
    setBusy(false);
  };

  return (
    <section className="stage rec">
      <span className="label">Class Recording</span>
      {embed && (
        <div className="player">
          <iframe ref={iframeRef} src={embed} title={lesson.title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, borderRadius: 12 }} />
        </div>
      )}
      {file && (
        <div className="player">
          <video ref={videoRef} src={lesson.recording_link} controls
            onLoadedMetadata={(e) => { const at = resumeTarget(e.currentTarget.duration); if (at) { e.currentTarget.currentTime = at; setResumedAt(at); } }}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: 12, background: "#000" }}
            onTimeUpdate={(e) => onTime(e.currentTarget.currentTime, e.currentTarget.duration)} onPause={save} onEnded={save} />
        </div>
      )}
      {!measured && (
        <a className="player" href={lesson.recording_link} target="_blank" rel="noopener noreferrer" aria-label="Open the recording in a new tab">
          <span><svg width="26" height="26" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg></span>
        </a>
      )}
      {measured && resumedAt !== null && (
        <div className="watch-meta"><span>Picked up where you stopped, at {clock(resumedAt)}</span><button className="link-btn" type="button" onClick={startOver}>Start from the Beginning</button></div>
      )}
      {measured && !lesson.attended && lesson.tracked !== false && (
        <>
          <div className="watchbar" role="progressbar" aria-valuenow={percent} aria-valuemin="0" aria-valuemax="100" aria-label="Recording watched">
            <i style={{ width: `${percent}%` }} /><em style={{ left: `${lesson.attend_percent ?? ATTEND_PERCENT}%` }} />
          </div>
          <div className="watch-meta"><span>Watched {percent}%</span><span>Watching {lesson.attend_percent ?? ATTEND_PERCENT}% marks your attendance</span></div>
        </>
      )}
      {!measured && !lesson.complete && lesson.tracked !== false && (
        <div className="row-btns">
          <button className="btn primary" type="button" onClick={confirmWatched} disabled={busy}>{busy ? "Saving…" : "I've watched the recording"}</button>
        </div>
      )}
      <small>Recorded {fmtDayTime(lesson.start_time)}{!measured ? " · opens on another site" : ""}</small>
    </section>
  );
}
